import { Icon } from '../../../atoms';
import { styled, useTheme } from '@mui/material/styles';
import { AdminFilterDropdown, OptionType } from '../Dropdown';
import { useDropdownState } from '../../../../common/hooks/useDropdownState';
import { useId } from 'react';

type Props = {
    label?: string;
    value?: string | number;
    name: string;
    options: OptionType<string | undefined>[];
    maxWidth?: number;
    // eslint-disable-next-line
    onChange(value?: string): void;
};

export const SelectInput = ({ label, value, name, options, maxWidth, onChange }: Props) => {
    const theme = useTheme();
    const { toggle, isActive, setAnchorEl, ...dropdownProps } = useDropdownState();
    const valueLabel = options?.find((option) => option.value === value)?.label;
    const listboxId = useId();

    const handleChange = (value: OptionType<string | undefined>['value']) => {
        onChange(value);
        toggle();
    };

    const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isActive) {
                setAnchorEl(e.currentTarget);
            }
        } else if (e.key === 'Escape') {
            setAnchorEl(null);
        }
    };

    return (
        <>
            <Container
                id={name}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isActive}
                aria-controls={listboxId}
                aria-label={label}
                tabIndex={0}
                onClick={toggle}
                onKeyDown={handleTriggerKeyDown}
                sx={maxWidth ? { width: maxWidth, minWidth: maxWidth } : undefined}
            >
                <Content>
                    {label && <Label>{label}:</Label>}
                    <Value>{valueLabel}</Value>
                </Content>
                <IconBox>
                    <Icon id="chevron-down" size={16} fill={theme.palette.semantic.icon.brand} />
                </IconBox>
            </Container>

            <AdminFilterDropdown
                {...dropdownProps}
                placement="bottom-end"
                options={options}
                width={maxWidth ? maxWidth : 240}
                onSelectOption={({ value }) => handleChange(value)}
                selectedOption={value}
                listboxId={listboxId}
                listboxLabel={label}
            />
        </>
    );
};

const Container = styled('div')(({ theme }) => ({
    display: 'flex',
    width: '240px',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '4px',
    border: `1px solid ${theme.palette.neutral[500]}`,
    background: theme.palette.semantic.bg.default,
    height: '40px',
    padding: '0 0 0 8px',
    cursor: 'pointer'
}));

const Content = styled('div')(() => ({
    flex: 1,
    minWidth: 0,
    gap: '4px',
    display: 'flex',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
}));

const Label = styled('span')(({ theme }: any) => ({
    ...theme.typography.labels.large,
    fontWeight: 400,
    lineHeight: '22px',
    color: theme.palette.semantic.text.muted
}));

const Value = styled('span')(({ theme }: any) => ({
    ...theme.typography.labels.large,
    fontWeight: 400,
    lineHeight: '22px',
    color: `${theme.palette.semantic.text.default}!important`,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
}));

const IconBox = styled('span')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    paddingRight: '4px',
    flexShrink: 0
}));
